// Operators — the console's own accounts (admin only).
//
// The server is the authority: /api/v1/users* is admin-gated in rbac.go, and a
// viewer who reaches this URL directly gets 403s. This page decides only what to
// OFFER, so a read-only operator is not shown buttons that cannot work.
//
// Deliberately absent: "reset someone's password". The API has no such route —
// an admin resetting a colleague deletes and recreates the account, producing a
// fresh must-rotate credential and an audit trail rather than silently taking
// over an existing identity.
import { useCallback, useEffect, useState } from "react";
import Button from "@/components/Button";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/lib/auth/AuthProvider";

interface UserInfo {
  email: string;
  role: string;
  must_change_password: boolean;
  created_at: string;
  password_changed_at: string;
}

const MIN_LEN = 12;

export default function Operators() {
  const { token, identity, onUnauthorized } = useAuth();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newRole, setNewRole] = useState("viewer");

  const authHeaders = useCallback(
    (): HeadersInit => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token ?? ""}`,
    }),
    [token],
  );

  // Raw fetch (this page predates the typed client) — the typed client's
  // useApi() drops the token + redirects to /login on any 401 via its own
  // middleware; this page needs the same behavior wired by hand, or a dead
  // token here just shows an error inline forever instead of sending the
  // user back to sign in.
  const authFetch = useCallback(
    async (path: string, init?: RequestInit): Promise<Response> => {
      const res = await fetch(`${import.meta.env.BASE_URL}${path}`, {
        ...init,
        headers: { ...authHeaders(), ...(init?.headers ?? {}) },
      });
      if (res.status === 401) onUnauthorized();
      return res;
    },
    [authHeaders, onUnauthorized],
  );

  // Surfaces the server's RFC 7807 `detail` when there is one. The server's
  // wording is more specific than anything we could invent here — e.g. it is the
  // side that knows this is the last admin.
  // Appends request_id (also the X-Request-Id response header) when present —
  // 500 bodies are deliberately generic, so it's the only thing a user can
  // hand support to correlate against.
  const explain = async (res: Response, fallback: string): Promise<string> => {
    try {
      const body = await res.json();
      const message = typeof body.detail === "string" ? body.detail : `${fallback} (${res.status})`;
      return typeof body.request_id === "string" ? `${message} (ref: ${body.request_id})` : message;
    } catch {
      return `${fallback} (${res.status})`;
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await authFetch("api/v1/users");
      if (!res.ok) throw new Error(await explain(res, "Could not load operators"));
      const body = (await res.json()) as { users: UserInfo[] };
      setUsers(
        [...(body.users ?? [])].sort((a, b) => a.email.localeCompare(b.email)),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy("create");
    try {
      const res = await authFetch("api/v1/users", {
        method: "POST",
        body: JSON.stringify({
          email: newEmail.trim(),
          password: newPass,
          role: newRole,
        }),
      });
      if (!res.ok) throw new Error(await explain(res, "Could not create the account"));
      setNewEmail("");
      setNewPass("");
      setNewRole("viewer");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const setRole = async (email: string, role: string) => {
    setErr(null);
    setBusy(email);
    try {
      const res = await authFetch(
        `api/v1/users/${encodeURIComponent(email)}`,
        { method: "PATCH", body: JSON.stringify({ role }) },
      );
      if (!res.ok) throw new Error(await explain(res, "Could not change the role"));
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (email: string) => {
    if (
      !window.confirm(
        `Remove ${email}? They lose access immediately — any signed-in session stops working on its next request.`,
      )
    ) {
      return;
    }
    setErr(null);
    setBusy(email);
    try {
      const res = await authFetch(
        `api/v1/users/${encodeURIComponent(email)}`,
        { method: "DELETE" },
      );
      if (!res.ok) throw new Error(await explain(res, "Could not remove the account"));
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const isSelf = (email: string) =>
    identity?.subject?.toLowerCase() === email.toLowerCase();
  const adminCount = users.filter((u) => u.role === "admin").length;
  const weakNew = newPass.length > 0 && newPass.length < MIN_LEN;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Operators"
        subtitle="Who can sign in to this console. Admins can change everything; read-only
          operators can view the fleet but cannot modify it, manage accounts, or
          see the SSO configuration."
      />

      {err && (
        <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {err}
        </div>
      )}

      <div className="rounded-lg border border-border bg-surface-elev">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-text-muted">
              <th className="px-4 py-2 font-medium">Email</th>
              <th className="px-4 py-2 font-medium">Role</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Password changed</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-text-muted">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-text-muted">
                  No accounts.
                </td>
              </tr>
            )}
            {users.map((u) => {
              // The server refuses to remove or demote the last admin — an
              // unrecoverable state without editing a file on the host. Disable
              // the controls so the refusal is predicted, not just reported.
              const lastAdmin = u.role === "admin" && adminCount === 1;
              const rowBusy = busy === u.email;
              return (
                <tr key={u.email} className="border-b border-border last:border-0">
                  <td className="px-4 py-2">
                    {u.email}
                    {isSelf(u.email) && (
                      <span className="ml-2 text-xs text-text-muted">(you)</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <select
                      className="rounded border border-border bg-surface px-2 py-1 text-sm"
                      value={u.role}
                      disabled={rowBusy || lastAdmin}
                      title={
                        lastAdmin
                          ? "This is the only admin. Promote someone else first."
                          : undefined
                      }
                      onChange={(e) => void setRole(u.email, e.target.value)}
                    >
                      <option value="admin">admin</option>
                      <option value="viewer">read-only</option>
                    </select>
                  </td>
                  <td className="px-4 py-2">
                    {u.must_change_password ? (
                      <span className="text-warning">Must change password</span>
                    ) : (
                      <span className="text-text-muted">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-text-muted">
                    {u.password_changed_at
                      ? new Date(u.password_changed_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="danger"
                      disabled={rowBusy || isSelf(u.email) || lastAdmin}
                      title={
                        isSelf(u.email)
                          ? "You cannot remove your own account."
                          : lastAdmin
                            ? "This is the only admin."
                            : undefined
                      }
                      onClick={() => void remove(u.email)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <form
        onSubmit={create}
        className="max-w-2xl space-y-3 rounded-lg border border-border bg-surface-elev p-4"
      >
        <div className="text-sm font-medium">Add an operator</div>
        <div className="flex flex-wrap gap-3">
          <label className="block flex-1">
            <span className="mb-1 block text-xs text-text-muted">Email</span>
            <input
              required
              type="email"
              className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </label>
          <label className="block flex-1">
            <span className="mb-1 block text-xs text-text-muted">
              Initial password
            </span>
            <input
              required
              type="password"
              autoComplete="new-password"
              className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-text-muted">Role</span>
            <select
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
            >
              <option value="viewer">read-only</option>
              <option value="admin">admin</option>
            </select>
          </label>
        </div>
        {weakNew && (
          <div className="text-xs text-danger">
            The initial password must be at least {MIN_LEN} characters.
          </div>
        )}
        <div className="text-xs text-text-muted">
          They must change this password the first time they sign in — you know it,
          so it cannot stay in use.
        </div>
        <Button
          type="submit"
          variant="primary"
          disabled={busy === "create" || weakNew}
        >
          {busy === "create" ? "Adding…" : "Add operator"}
        </Button>
      </form>
    </div>
  );
}
